import { useQueryClient } from "@tanstack/react-query"
import { useNavigate } from "@tanstack/react-router"
import { format, parseISO } from "date-fns"
import { useCallback, useMemo } from "react"
import { toast } from "sonner"

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Skeleton } from "@/components/ui/skeleton"
import { cn } from "@/lib/utils"
import {
  useIsSavingMembershipApplicationDraft,
  useMembershipApplicationDraft,
  useSaveMembershipApplicationDraft,
  type MembershipApplicationDraft,
  type SupportingDocumentDraft,
} from "../api/useMembershipApplicationDraft"
import { membershipApplicationSteps } from "../constants"
import { useApplicationCategoryConfiguration } from "../hooks/useApplicationConfiguration"
import type { MembershipApplicationStepId } from "../types"
import {
  useCreateApplication,
  getGetApplicationsQueryKey,
  getGetMyApplicationsQueryKey,
} from "@/utils/api/membership-service"
import { buildApplicationPayload } from "../utils/build-application-payload"
import type { AxiosError } from "axios"
import { AlertTriangle, CheckCircle2, FileText } from "lucide-react"

const stepById = membershipApplicationSteps.reduce(
  (acc, step) => {
    acc[step.id] = step
    return acc
  },
  {} as Record<MembershipApplicationStepId, (typeof membershipApplicationSteps)[number]>,
)

export function ReviewStep() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { data: draft, isLoading } = useMembershipApplicationDraft()
  const saveDraftMutation = useSaveMembershipApplicationDraft()
  const isSavingDraft = useIsSavingMembershipApplicationDraft()
  const createApplicationMutation = useCreateApplication()

  const categoryId = draft?.category?.categoryId ?? null
  const { requiredStepIds, configuration, category } = useApplicationCategoryConfiguration(categoryId)
  const actionableRequiredStepIds = useMemo(
    () => requiredStepIds.filter((stepId) => stepId !== "review"),
    [requiredStepIds],
  )

  const completedSet = useMemo(
    () => new Set<MembershipApplicationStepId>(draft?.completedStepIds ?? []),
    [draft?.completedStepIds],
  )

  const outstandingSteps = useMemo(
    () => actionableRequiredStepIds.filter((id) => !completedSet.has(id)),
    [actionableRequiredStepIds, completedSet],
  )

  const isSubmitted = draft?.status === "submitted"
  const isReadyToSubmit = !isSubmitted && outstandingSteps.length === 0

  const profileSection = configuration?.sections.profile
  // const employmentSection = configuration?.sections.employment
  // const educationSection = configuration?.sections.education
  // const affiliationsSection = configuration?.sections.affiliations
  const documentsSection = configuration?.sections.documents

  // const showEmployment = employmentSection?.isVisible !== false
  // const showEducation = educationSection?.isVisible !== false
  // const showAffiliations = affiliationsSection?.isVisible !== false
  const showDocuments = documentsSection?.isVisible !== false

  const handleSubmit = useCallback(async () => {
    if (!draft) {
      toast.error("We could not find your application details. Please refresh and try again.")
      return
    }

    if (!isReadyToSubmit) {
      toast.error("Complete all sections before submitting.")
      return
    }

    try {
      const payload = buildApplicationPayload({
        draft,
        category,
        categoryName: category?.categoryName ?? null,
      })

      await createApplicationMutation.mutateAsync({ data: payload })

      await saveDraftMutation.mutateAsync({ status: "submitted" })

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: getGetMyApplicationsQueryKey() }),
        queryClient.invalidateQueries({ queryKey: getGetApplicationsQueryKey() }),
      ])

      toast.success("Application submitted successfully. Our team will review it shortly.")
      navigate({ to: '/login', replace: true })
    } catch (error) {
      console.error("Application submission failed:", error)

      const axiosError = error as AxiosError<{ message?: string }>
      const apiMessage = axiosError?.response?.data?.message
      const fallbackMessage = axiosError?.message ?? (error instanceof Error ? error.message : undefined)

      toast.error(apiMessage ?? fallbackMessage ?? "Unable to submit application. Please try again.")
    }
  }, [
    draft,
    isReadyToSubmit,
    createApplicationMutation,
    saveDraftMutation,
    queryClient,
    navigate,
    category,
  ])

  if (isLoading || !draft) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-64" />
        </CardHeader>
        <CardContent className="space-y-4">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
        </CardContent>
      </Card>
    )
  }

  return (
    <section className="space-y-6">
      <header className="space-y-2">
        <h1 className="text-2xl font-semibold">Review and submit</h1>
        <p className="text-sm text-muted-foreground">
          Confirm your details below. You can still go back and edit individual sections before submitting.
        </p>
      </header>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1.75fr)_minmax(280px,1fr)] xl:grid-cols-[minmax(0,1.9fr)_minmax(320px,1fr)]">
        <div className="space-y-6">
          {isSubmitted ? (
            <Alert>
              <AlertTitle>Application already submitted</AlertTitle>
              <AlertDescription>
                Your application has been submitted for review. You can review the details below but additional changes may
                require contacting support.
              </AlertDescription>
            </Alert>
          ) : null}

          <SummarySection
            title="Membership category"
            description="Make sure your selected category is accurate."
            onEdit={() => navigate({ to: stepById.category.path })}
            content={<CategorySummary draft={draft} categoryName={category?.categoryName} />}
          />

          <SummarySection
            title="Membership category & personal information"
            description={
              profileSection?.guidance ?? "Make sure your contact details and selected category are accurate."
            }
            onEdit={() => navigate({ to: stepById.profile.path })}
            content={<ProfileSummary draft={draft} />}
          />

          {/* {showEmployment ? (
            <SummarySection
              title={
                employmentSection?.isRequired === false
                  ? "Employment history (optional)"
                  : "Employment history"
              }
              description={
                employmentSection?.guidance ??
                "Confirm that employment dates and responsibilities reflect your experience."
              }
              onEdit={() => navigate({ to: stepById.employment.path })}
              content={<EmploymentSummary draft={draft} />}
            />
          ) : null} */}

          {/* {showEducation ? (
            <SummarySection
              title={
                educationSection?.isRequired === false
                  ? "Academic qualifications (optional)"
                  : "Academic qualifications"
              }
              description={
                educationSection?.guidance ??
                "Ensure the key qualifications supporting your application are listed."
              }
              onEdit={() => navigate({ to: stepById.education.path })}
              content={<EducationSummary draft={draft} />}
            />
          ) : null} */}

          {/* {showAffiliations ? (
            <SummarySection
              title={
                affiliationsSection?.isRequired === false
                  ? "Affiliations & awards (optional)"
                  : "Affiliations & awards"
              }
              description={
                affiliationsSection?.guidance ??
                "Highlight membership in professional bodies and recognitions."
              }
              onEdit={() => navigate({ to: stepById.affiliations.path })}
              content={<AffiliationsSummary draft={draft} />}
            />
          ) : null} */}
        </div>

        <aside className="space-y-6">
          <SubmissionChecklistPanel
            outstandingSteps={outstandingSteps}
            isSubmitted={isSubmitted}
            isReadyToSubmit={isReadyToSubmit}
            onNavigate={(stepId) => navigate({ to: stepById[stepId].path })}
          />
          {showDocuments ? (
            <DocumentsSummaryPanel
              draft={draft}
              isOptional={documentsSection?.isRequired === false}
              onEdit={() => navigate({ to: stepById.documents.path })}
            />
          ) : null}
        </aside>
      </div>

      <div className="flex flex-col items-start gap-2 sm:flex-row sm:items-center sm:justify-end">
        <Button
          type="button"
          onClick={handleSubmit}
          disabled={
            isSubmitted
            || !isReadyToSubmit
            || createApplicationMutation.isPending
            || isSavingDraft
            || saveDraftMutation.isPending
          }
        >
          {isSubmitted
            ? "Application submitted"
            : createApplicationMutation.isPending || isSavingDraft || saveDraftMutation.isPending
              ? "Submitting..."
              : "Submit application"}
        </Button>
      </div>
    </section>
  )
}

interface SummarySectionProps {
  title: string
  description: string
  content: React.ReactNode
  onEdit: () => void
}

function SummarySection({ title, description, content, onEdit }: SummarySectionProps) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-4">
        <div className="space-y-1">
          <CardTitle className="text-lg">{title}</CardTitle>
          <CardDescription>{description}</CardDescription>
        </div>
        <Button type="button" variant="outline" size="sm" onClick={onEdit}>
          Edit
        </Button>
      </CardHeader>
      <CardContent>{content}</CardContent>
    </Card>
  )
}

interface SummaryProps {
  draft: MembershipApplicationDraft
}

interface CategorySummaryProps extends SummaryProps {
  categoryName?: string | null
}


function CategorySummary({ draft, categoryName }: CategorySummaryProps) {
  const category = draft.category
  if (!category) {
    return <EmptySummary message="Category information is missing." />
  }

  return (
    <dl className="grid gap-3 sm:grid-cols-2">
      <SummaryRow
        term="Category"
        value={categoryName ?? (category.categoryId ? category.categoryId.toString() : "Not provided")}
      />
      <SummaryRow term="Prerequisites acknowledged" value={category.prerequisitesAccepted ? "Yes" : "No"} />
    </dl>
  )
}

function ProfileSummary({ draft }: SummaryProps) {
  const profile = draft.profile
  if (!profile) {
    return <EmptySummary message="Profile information is missing." />
  }

  return (
    <dl className="grid gap-3 sm:grid-cols-2">
      <SummaryRow term="First name" value={profile.firstName || "Not provided"} />
      <SummaryRow term="Last name" value={profile.lastName || "Not provided"} />
      <SummaryRow term="Middle name" value={profile.middleName || "Not provided"} />
      <SummaryRow term="Organisation" value={profile.organisation || "Not provided"} />
      <SummaryRow term="Email" value={profile.email || "Not provided"} />
      <SummaryRow term="Phone" value={profile.phoneNumber || "Not provided"} />
      <SummaryRow term="Date of birth" value={formatDate(profile.dateOfBirth)} />
      <SummaryRow term="Gender" value={profile.gender ?? "Not provided"} />
      <SummaryRow
        term="LinkedIn Profile link"
        value={
          profile.profileLinkUrl
            ? (
                <a
                  href={profile.profileLinkUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary underline underline-offset-4 hover:text-primary/80"
                >
                  {profile.profileLinkUrl}
                </a>
              )
            : "Not provided"
        }
      />
    </dl>
  )
}

// function EmploymentSummary({ draft }: SummaryProps) {
//   const entries = draft.employmentHistory
//   if (!entries || entries.length === 0) {
//     return <EmptySummary message="No employment records captured yet." />
//   }

//   return (
//     <div className="space-y-4">
//       {entries.map((entry, index) => (
//         <div key={entry.id ?? index} className="rounded-md border border-border p-4">
//           <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
//             <p className="font-medium">{entry.jobTitle || "Job title not specified"}</p>
//             <p className="text-sm text-muted-foreground">{entry.organisation || "Organisation not specified"}</p>
//           </div>
//           <p className="text-sm text-muted-foreground">
//             {formatDate(entry.startDate)} - {entry.currentlyEmployed ? "Present" : formatDate(entry.endDate)}
//           </p>
//           {entry.responsibilities ? (
//             <p className="mt-2 text-sm">
//               <span className="font-medium">Responsibilities: </span>
//               {entry.responsibilities}
//             </p>
//           ) : null}
//           {entry.reportingTo ? (
//             <p className="mt-1 text-sm text-muted-foreground">Reporting to: {entry.reportingTo}</p>
//           ) : null}
//         </div>
//       ))}
//     </div>
//   )
// }

// function EducationSummary({ draft }: SummaryProps) {
//   const entries = draft.educationHistory
//   if (!entries || entries.length === 0) {
//     return <EmptySummary message="No academic qualifications captured yet." />
//   }

//   return (
//     <div className="space-y-4">
//       {entries.map((entry, index) => (
//         <div key={entry.id ?? index} className="rounded-md border border-border p-4">
//           <p className="font-medium">{entry.qualification || "Qualification not specified"}</p>
//           <p className="text-sm text-muted-foreground">{entry.institution || "Institution not specified"}</p>
//           <p className="text-sm text-muted-foreground">
//             Awarding body: {entry.awardingBody || "Not provided"}
//             {entry.graduationYear ? ` - Year: ${entry.graduationYear}` : ""}
//           </p>
//           {entry.fieldOfStudy ? (
//             <p className="text-sm text-muted-foreground">Field of study: {entry.fieldOfStudy}</p>
//           ) : null}
//           {entry.honors ? <p className="text-sm">Honours: {entry.honors}</p> : null}
//         </div>
//       ))}
//     </div>
//   )
// }

// function AffiliationsSummary({ draft }: SummaryProps) {
//   const affiliations = draft.professionalAffiliations
//   const awards = draft.professionalAwards

//   if ((affiliations?.length ?? 0) === 0 && (awards?.length ?? 0) === 0) {
//     return <EmptySummary message="No affiliations or recognitions captured yet." />
//   }

//   return (
//     <div className="space-y-6">
//       {affiliations && affiliations.length > 0 ? (
//         <section className="space-y-3">
//           <h3 className="text-sm font-semibold uppercase text-muted-foreground">Professional bodies</h3>
//           <div className="space-y-3">
//             {affiliations.map((affiliation, index) => (
//               <div key={affiliation.id ?? index} className="rounded-md border border-border p-4">
//                 <p className="font-medium">{affiliation.organisation || "Organisation not specified"}</p>
//                 <p className="text-sm text-muted-foreground">
//                   {affiliation.role ? `${affiliation.role} - ` : ""}
//                   {affiliation.membershipNumber ? `Membership no. ${affiliation.membershipNumber}` : "No membership number"}
//                 </p>
//                 {affiliation.since ? (
//                   <p className="text-sm text-muted-foreground">Since {formatDate(affiliation.since)}</p>
//                 ) : null}
//                 {affiliation.notes ? <p className="text-sm">Notes: {affiliation.notes}</p> : null}
//               </div>
//             ))}
//           </div>
//         </section>
//       ) : null}

//       {awards && awards.length > 0 ? (
//         <section className="space-y-3">
//           <h3 className="text-sm font-semibold uppercase text-muted-foreground">Recognitions & awards</h3>
//           <div className="space-y-3">
//             {awards.map((award, index) => (
//               <div key={award.id ?? index} className="rounded-md border border-border p-4">
//                 <p className="font-medium">{award.title || "Title not specified"}</p>
//                 <p className="text-sm text-muted-foreground">
//                   Issued by {award.issuer || "Not provided"}
//                   {award.awardedOn ? ` - ${formatDate(award.awardedOn)}` : ""}
//                 </p>
//                 {award.description ? <p className="text-sm">{award.description}</p> : null}
//               </div>
//             ))}
//           </div>
//         </section>
//       ) : null}
//     </div>
//   )
// }

function SubmissionChecklistPanel({
  outstandingSteps,
  isSubmitted,
  isReadyToSubmit,
  onNavigate,
}: {
  outstandingSteps: MembershipApplicationStepId[]
  isSubmitted: boolean
  isReadyToSubmit: boolean
  onNavigate: (stepId: MembershipApplicationStepId) => void
}) {
  if (isSubmitted) {
    return (
      <Card className="border-emerald-500/40 bg-emerald-500/10">
        <CardHeader className="space-y-1">
          <CardTitle className="flex items-center gap-2 text-base font-semibold text-emerald-800 dark:text-emerald-200">
            <CheckCircle2 className="h-4 w-4" />
            Submitted for review
          </CardTitle>
          <CardDescription className="text-xs">
            We’ve received your application. If you need to amend anything, contact the membership team.
          </CardDescription>
        </CardHeader>
      </Card>
    )
  }

  if (outstandingSteps.length > 0) {
    const outstandingDetails = outstandingSteps
      .map((stepId) => stepById[stepId])
      .filter((step): step is (typeof membershipApplicationSteps)[number] => Boolean(step))

    return (
      <Card className="border-amber-500/40 bg-amber-500/10">
        <CardHeader className="space-y-1">
          <CardTitle className="flex items-center gap-2 text-base font-semibold text-amber-800 dark:text-amber-100">
            <AlertTriangle className="h-4 w-4" />
            Finish your checklist
          </CardTitle>
          <CardDescription className="text-xs">
            Complete the remaining sections before you submit your application.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <ul className="space-y-2">
            {outstandingDetails.map((step) => (
              <li
                key={step.id}
                className="flex items-center justify-between gap-2 rounded-md border border-amber-500/30 bg-background/80 px-3 py-2"
              >
                <span className="text-sm font-medium text-foreground">{step.title}</span>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className="text-amber-800 hover:text-amber-900 dark:text-amber-100"
                  onClick={() => onNavigate(step.id as MembershipApplicationStepId)}
                >
                  Continue
                </Button>
              </li>
            ))}
          </ul>
          <p className="text-xs text-amber-900/80 dark:text-amber-100/80">
            You’ll be able to submit once everything above is completed.
          </p>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className="border-emerald-500/40 bg-emerald-500/10">
      <CardHeader className="space-y-1">
        <CardTitle className="flex items-center gap-2 text-base font-semibold text-emerald-800 dark:text-emerald-200">
          <CheckCircle2 className="h-4 w-4" />
          Ready to submit
        </CardTitle>
        <CardDescription className="text-xs">
          All required sections are complete. Give everything a final look, then submit when you’re confident.
        </CardDescription>
      </CardHeader>
      {!isReadyToSubmit ? null : (
        <CardContent>
          <p className="text-xs text-emerald-900/80 dark:text-emerald-100/80">
            If you spot anything to update, use the edit buttons in each section.
          </p>
        </CardContent>
      )}
    </Card>
  )
}

function DocumentsSummaryPanel({
  draft,
  onEdit,
  isOptional,
}: {
  draft: MembershipApplicationDraft
  onEdit: () => void
  isOptional?: boolean
}) {
  const documents = draft.supportingDocuments ?? []
  const total = documents.length
  const uploaded = documents.filter((doc) => doc.status === "uploaded").length
  const pendingCount = Math.max(total - uploaded, 0)
  const pendingDocuments = documents.filter((doc) => doc.status !== "uploaded")

  return (
    <Card className="lg:sticky lg:top-6">
      <CardHeader className="flex flex-row items-start justify-between gap-4">
        <div className="space-y-1">
          <CardTitle className="flex items-center gap-2 text-lg">
            Supporting documents
            {isOptional ? (
              <Badge variant="outline" className="text-[10px] font-semibold uppercase tracking-wide">
                Optional
              </Badge>
            ) : null}
          </CardTitle>
          <CardDescription>
            Track which files are already attached and what’s still pending before you submit.
          </CardDescription>
        </div>
        <Button type="button" variant="outline" size="sm" onClick={onEdit}>
          Manage
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-3 gap-2">
          <DocumentStat label="Total" value={total} />
          <DocumentStat label="Uploaded" value={uploaded} tone="success" />
          <DocumentStat label="Pending" value={pendingCount} tone={pendingCount > 0 ? "warning" : "muted"} />
        </div>

        {total === 0 ? (
          <div className="space-y-3">
            <EmptySummary message="No supporting documents recorded yet." />
            <PlanCallout
              tone="warning"
              title="Next step"
              description="Return to the supporting documents step to attach any required files."
            />
          </div>
        ) : (
          <>
            <DocumentsSummaryList documents={documents} />
            {pendingDocuments.length > 0 ? (
              <PlanCallout
                tone="warning"
                title="Outstanding files"
                description="Attach the remaining files so reviewers can process your application without delays."
                items={pendingDocuments.map((doc) => doc.documentType || "Supporting document")}
              />
            ) : (
              <PlanCallout
                tone="success"
                title="All files uploaded"
                description="Everything looks good. You can submit once you’ve reviewed the other sections."
              />
            )}
          </>
        )}
      </CardContent>
    </Card>
  )
}

function DocumentsSummaryList({ documents }: { documents: SupportingDocumentDraft[] }) {
  if (documents.length === 0) {
    return null
  }

  return (
    <ul className="space-y-3">
      {documents.map((doc, index) => {
        const isUploaded = doc.status === "uploaded"
        return (
          <li
            key={doc.id ?? `${doc.documentType ?? "document"}-${index}`}
            className="rounded-md border border-border/70 bg-card/50 p-3 shadow-xs"
          >
            <div className="flex items-start gap-3">
              <FileText className="mt-0.5 h-4 w-4 text-muted-foreground" />
              <div className="flex-1 space-y-1">
                <p className="text-sm font-medium text-foreground">
                  {doc.documentType || "Supporting document"}
                </p>
                <p className="text-xs text-muted-foreground">
                  {doc.originalFilename || doc.fileName || "File name pending"}
                </p>
                {doc.notes ? (
                  <p className="text-xs text-muted-foreground">Notes: {doc.notes}</p>
                ) : null}
              </div>
              <Badge
                variant="outline"
                className={cn(
                  "text-[10px] font-semibold uppercase tracking-wide",
                  isUploaded
                    ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-200"
                    : "border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-100",
                )}
              >
                {isUploaded ? "Uploaded" : "Pending"}
              </Badge>
            </div>
            {isUploaded && doc.fileUrl ? (
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <Button asChild size="xs" variant="secondary">
                  <a href={doc.fileUrl} target="_blank" rel="noreferrer">
                    View file
                  </a>
                </Button>
                {doc.fileName ? (
                  <span className="text-xs text-muted-foreground">{doc.fileName}</span>
                ) : null}
              </div>
            ) : null}
          </li>
        )
      })}
    </ul>
  )
}

function DocumentStat({
  label,
  value,
  tone = "default",
}: {
  label: string
  value: number
  tone?: "default" | "success" | "warning" | "muted"
}) {
  const toneClasses = {
    default: "border-border/60 bg-muted/40 text-foreground",
    success: "border-emerald-500/50 bg-emerald-500/10 text-emerald-700 dark:text-emerald-200",
    warning: "border-amber-500/50 bg-amber-500/10 text-amber-800 dark:text-amber-100",
    muted: "border-border/50 bg-muted/20 text-muted-foreground",
  } as const

  return (
    <div className={cn("rounded-md border p-3 text-center", toneClasses[tone])}>
      <p className="text-[11px] font-semibold uppercase tracking-wide">{label}</p>
      <p className="mt-1 text-lg font-semibold">{value}</p>
    </div>
  )
}

function PlanCallout({
  tone,
  title,
  description,
  items,
}: {
  tone: "success" | "warning"
  title: string
  description: string
  items?: string[]
}) {
  const isSuccess = tone === "success"
  const Icon = isSuccess ? CheckCircle2 : AlertTriangle
  return (
    <div
      className={cn(
        "rounded-md border px-3 py-3 text-sm shadow-xs",
        isSuccess
          ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-800 dark:text-emerald-100"
          : "border-amber-500/40 bg-amber-500/10 text-amber-800 dark:text-amber-100",
      )}
    >
      <div className="flex items-start gap-2">
        <Icon className="mt-0.5 h-4 w-4" />
        <div className="space-y-1">
          <p className="text-sm font-semibold">{title}</p>
          <p className="text-xs leading-relaxed text-foreground/80">{description}</p>
          {items && items.length > 0 ? (
            <ul className="mt-2 list-disc space-y-1 pl-4">
              {items.map((item) => (
                <li key={item} className="text-xs text-foreground/80">
                  {item}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      </div>
    </div>
  )
}

function SummaryRow({ term, value }: { term: string; value: React.ReactNode }) {
  return (
    <div className="space-y-1 rounded-md border border-border p-3">
      <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{term}</dt>
      <dd className="text-sm font-medium text-foreground">{value || "Not provided"}</dd>
    </div>
  )
}

function EmptySummary({ message }: { message: string }) {
  return <p className="text-sm text-muted-foreground">{message}</p>
}

function formatDate(value?: string | null) {
  if (!value) return "Not provided"
  try {
    const parsed = parseISO(value)
    if (Number.isNaN(parsed.getTime())) {
      return value
    }
    return format(parsed, "dd MMM yyyy")
  } catch (_error) {
    return value
  }
}
